import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { BrowsePage, BrowseParams } from "@/lib/api";

const PAGE_SIZES = [5, 10, 25] as const;
type PageSize = (typeof PAGE_SIZES)[number];

const BROWSING_PREFIX = "esumei:browsing:";

export function clearBrowsingState(): void {
  for (const key of Object.keys(sessionStorage)) {
    if (key.startsWith(BROWSING_PREFIX)) sessionStorage.removeItem(key);
  }
}

export function useBrowsingState<T>(key: string | undefined, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const storageKey = key ? `${BROWSING_PREFIX}${key}` : null;
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = storageKey ? sessionStorage.getItem(storageKey) : null;
      return stored === null ? initial : JSON.parse(stored) as T;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    if (storageKey) sessionStorage.setItem(storageKey, JSON.stringify(value));
  }, [storageKey, value]);
  return [value, setValue];
}

type SortOption<T> = { key: string; label: string; value: (row: T) => string };
type ListOptions<T> = { storageKey: string; sort: SortOption<T>[]; identity: (row: T) => string };

export type ListState<T> = {
  search: string;
  setSearch: (search: string) => void;
  rows: T[];
  total: number;
  page: number;
  pageCount: number;
  pageSize: PageSize;
  setPageSize: (pageSize: PageSize) => void;
  setPage: (page: number) => void;
  sort: string;
  setSort: (sort: string) => void;
  sortOptions: { key: string; label: string }[];
  direction: "asc" | "desc";
  setDirection: (direction: "asc" | "desc") => void;
};

export function useSearchableList<T>(
  rows: readonly T[],
  searchText: (row: T) => string,
  filterKey = "",
  options?: ListOptions<T>,
): ListState<T> {
  const [state, setState] = useBrowsingState<{ search: string; page: number; pageSize: PageSize; sort: string; direction: "asc" | "desc" }>(options?.storageKey, {
    search: "",
    page: 1,
    pageSize: 5,
    sort: options?.sort[0]?.key ?? "",
    direction: "asc",
  });
  const previousFilter = useRef(filterKey);
  useEffect(() => {
    if (previousFilter.current !== filterKey) {
      setState((previous) => ({ ...previous, page: 1 }));
      previousFilter.current = filterKey;
    }
  }, [filterKey, setState]);
  const query = state.search.trim().toLowerCase();
  const filtered = query
    ? rows.filter((row) => searchText(row).toLowerCase().includes(query))
    : rows;
  const sortOption = options?.sort.find((item) => item.key === state.sort) ?? options?.sort[0];
  const sorted = sortOption && options ? [...filtered].sort((a, b) =>
    (state.direction === "desc" ? -1 : 1) * sortOption.value(a).localeCompare(sortOption.value(b), undefined, { sensitivity: "base", numeric: true }) ||
    options.identity(a).localeCompare(options.identity(b), undefined, { numeric: true })) : filtered;
  const pageCount = Math.max(1, Math.ceil(sorted.length / state.pageSize));
  const page = Math.min(state.page, pageCount);
  return {
    search: state.search,
    setSearch: (search: string) => setState((previous) => ({ ...previous, search, page: 1 })),
    rows: sorted.slice((page - 1) * state.pageSize, page * state.pageSize),
    total: filtered.length,
    page,
    pageCount,
    pageSize: state.pageSize,
    setPageSize: (pageSize: PageSize) => setState((previous) => ({ ...previous, pageSize, page: 1 })),
    setPage: (next: number) => setState((previous) => ({
      ...previous,
      page: Math.max(1, Math.min(next, pageCount)),
    })),
    sort: sortOption?.key ?? "",
    setSort: (sort: string) => setState((previous) => ({ ...previous, sort, page: 1 })),
    sortOptions: options?.sort.map(({ key, label }) => ({ key, label })) ?? [],
    direction: state.direction ?? "asc",
    setDirection: (direction) => setState((previous) => ({ ...previous, direction, page: 1 })),
  };
}

export function usePagedList<T>(
  key: string,
  filters: BrowseParams,
  sortOptions: { key: string; label: string }[],
  fetchPage: (params: BrowseParams) => Promise<BrowsePage<T>>,
): { list: ListState<T>; query: UseQueryResult<BrowsePage<T>, Error> } {
  const [state, setState] = useBrowsingState(`${key}:list`, {
    search: "", page: 1, pageSize: 5 as PageSize, sort: sortOptions[0]?.key ?? "name", direction: "asc" as "asc" | "desc",
  });
  const filterKey = JSON.stringify(filters);
  const previousFilter = useRef(filterKey);
  const page = previousFilter.current === filterKey ? state.page : 1;
  useEffect(() => {
    if (previousFilter.current !== filterKey) {
      previousFilter.current = filterKey;
      setState((previous) => ({ ...previous, page: 1 }));
    }
  }, [filterKey, setState]);
  const params = { ...filters, search: state.search.trim(), page, page_size: state.pageSize, sort: state.sort, direction: state.direction ?? "asc" };
  const query = useQuery({ queryKey: [key, "browse", params], queryFn: () => fetchPage(params) });
  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / state.pageSize));
  useEffect(() => {
    if (query.data && page > pageCount) setState((previous) => ({ ...previous, page: pageCount }));
  }, [query.data, page, pageCount, setState]);
  return {
    query,
    list: {
      search: state.search,
      setSearch: (search) => setState((previous) => ({ ...previous, search, page: 1 })),
      rows: query.data?.items ?? [], total, page: Math.min(page, pageCount), pageCount,
      pageSize: state.pageSize,
      setPageSize: (pageSize) => setState((previous) => ({ ...previous, pageSize, page: 1 })),
      setPage: (next) => setState((previous) => ({ ...previous, page: Math.max(1, Math.min(next, pageCount)) })),
      sort: state.sort,
      setSort: (sort) => setState((previous) => ({ ...previous, sort, page: 1 })),
      sortOptions,
      direction: state.direction ?? "asc",
      setDirection: (direction) => setState((previous) => ({ ...previous, direction, page: 1 })),
    },
  };
}

export function ListSort({ sort, setSort, sortOptions, direction, setDirection }: Pick<ListState<unknown>, "sort" | "setSort" | "sortOptions" | "direction" | "setDirection">): JSX.Element | null {
  if (sortOptions.length === 0) return null;
  return <div className="flex flex-wrap gap-2"><label className="flex items-center gap-2 text-sm">
    Sort by
    <select aria-label="Sort by" className="h-9 rounded-md border border-input bg-background px-2"
      value={sort} onChange={(event) => setSort(event.target.value)}>
      {sortOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
    </select>
  </label><label className="flex items-center gap-2 text-sm">Order
    <select aria-label="Sort order" className="h-9 rounded-md border border-input bg-background px-2"
      value={direction} onChange={(event) => setDirection(event.target.value === "desc" ? "desc" : "asc")}>
      <option value="asc">Ascending</option><option value="desc">Descending</option>
    </select>
  </label></div>;
}

export function ListSearch({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <div className="relative w-full min-w-0 sm:max-w-sm">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="search"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 pl-9"
      />
    </div>
  );
}

export function ListPagination({
  total,
  page,
  pageCount,
  pageSize,
  setPageSize,
  setPage,
}: {
  total: number;
  page: number;
  pageCount: number;
  pageSize: PageSize;
  setPageSize: (pageSize: PageSize) => void;
  setPage: (page: number) => void;
}): JSX.Element {
  return (
    <nav
      aria-label="Table pagination"
      className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3"
    >
      <p aria-live="polite" className="text-sm text-muted-foreground">
        {total === 0
          ? "0 entries"
          : `${(page - 1) * pageSize + 1} to ${Math.min(page * pageSize, total)} of ${total} entries`}
      </p>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-sm">
          Rows per page
          <select
            className="h-9 rounded-md border border-input bg-background px-2"
            value={pageSize}
            onChange={(event) => {
              const selected = PAGE_SIZES.find((size) => size === Number(event.target.value));
              if (selected !== undefined) setPageSize(selected);
            }}
          >
            {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
        <Button
          variant="outline"
          size="sm"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" /> Previous
        </Button>
        <span className="text-sm tabular-nums">
          Page {page} of {pageCount}
        </span>
        <Button
          variant="outline"
          size="sm"
          aria-label="Next page"
          disabled={page >= pageCount}
          onClick={() => setPage(page + 1)}
        >
          Next <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </nav>
  );
}
